import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  arePartnerAcceptPlansValid,
  createDefaultPartnerAcceptPlans,
  PARTNER_PLAN_LABELS,
} from '../../utils/partnerAcceptPlans';

const PartnerAcceptPlansDialog = ({
  open,
  partnerName,
  submitting = false,
  onClose,
  onConfirm,
}) => {
  const [plans, setPlans] = useState(createDefaultPartnerAcceptPlans);

  useEffect(() => {
    if (open) {
      setPlans(createDefaultPartnerAcceptPlans());
    }
  }, [open]);

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  const handleAmountChange = (planKey) => (event) => {
    const nextValue = event.target.value;
    setPlans((current) =>
      current.map((plan) =>
        plan.key === planKey
          ? {
              ...plan,
              amount: nextValue === '' ? '' : Number(nextValue),
            }
          : plan,
      ),
    );
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!arePartnerAcceptPlansValid(plans)) return;
    onConfirm?.(plans);
  };

  const isValid = arePartnerAcceptPlansValid(plans);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Plan amounts</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
            Adjust plan amounts for{' '}
            <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {partnerName || 'this partner'}
            </Box>{' '}
            before completing approval.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {plans.map((plan) => (
              <TextField
                key={plan.key}
                label={PARTNER_PLAN_LABELS[plan.key] ?? plan.key}
                type="number"
                fullWidth
                required
                disabled={submitting}
                value={plan.amount}
                onChange={handleAmountChange(plan.key)}
                inputProps={{ min: 0, step: 1 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">AED</InputAdornment>,
                }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleClose} disabled={submitting} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            color="success"
            loading={submitting}
            disabled={!isValid}
            sx={{ textTransform: 'none', minWidth: 120 }}
          >
            Approve
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PartnerAcceptPlansDialog;
